import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, Loader2, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

export function MailingQueryExport() {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all matching records with pagination to bypass 1000-row limit
      let allRecords: any[] = [];
      let from = 0;
      const pageSize = 1000;
      
      while (true) {
        const { data, error } = await supabase
          .from("mailing_records")
          .select("first_name, last_name, street_address, city, state, zip_code, county")
          .eq("times_mailed", 0)
          .or("business_name.is.null,business_name.eq.")
          .or("state.eq.MI,origin_state.eq.MI")
          .eq("mailing_status", "active")
          .not("recorded_date", "is", null)
          .lte("recorded_date", "2025-09-01")
          .order("last_name", { ascending: true })
          .order("first_name", { ascending: true })
          .range(from, from + pageSize - 1);

        if (error) throw error;
        if (!data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      if (allRecords.length === 0) {
        toast.error("No records found matching criteria");
        setExporting(false);
        return;
      }

      // Format for Excel - proper case names
      const toProperCase = (s: string | null) => {
        if (!s) return "";
        return s.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
      };

      const rows = allRecords.map((r) => ({
        "First Name": toProperCase(r.first_name),
        "Last Name": toProperCase(r.last_name),
        "Street Address": r.street_address || "",
        "City": toProperCase(r.city),
        "State": (r.state || "").toUpperCase(),
        "Zip Code": (r.zip_code || "").split("-")[0],
        "County": toProperCase(r.county),
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths
      ws["!cols"] = [
        { wch: 18 }, // First Name
        { wch: 22 }, // Last Name
        { wch: 30 }, // Street Address
        { wch: 18 }, // City
        { wch: 8 },  // State
        { wch: 12 }, // Zip Code
        { wch: 16 }, // County
      ];

      XLSX.utils.book_append_sheet(wb, ws, "MI Never Mailed");
      XLSX.writeFile(wb, `MI_Never_Mailed_Individuals_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success(`Exported ${rows.length} records to Excel`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Michigan — Never Mailed Individuals (Recorded ≤ 9/1/2025)
        </CardTitle>
        <CardDescription>
          210 individual records in Michigan that have never been mailed, with recorded dates on or before September 1, 2025.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={handleExport} disabled={exporting} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? "Exporting..." : "Download Excel"}
        </Button>
      </CardContent>
    </Card>
  );
}
